import { useEffect, useRef, useState } from 'react';
import jsonpCall from '../JsonpCall';

function validDate(d) {
  if (Object.prototype.toString.call(d) === '[object Date]') {
    // it is a date
    if (!Number.isNaN(d.getTime())) {
      // d.valueOf() could also work
      return true;
    }
  }
  return false;
}

function getFormattedDate(theDate) {
  const date = new Date(theDate);
  date.setDate(date.getDate() + 1); // need to increment by 1 because of UTC stuff
  if (validDate(date)) {
    const year = date.getFullYear();
    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : `0${month}`;
    let day = date.getDate().toString();
    day = day.length > 1 ? day : `0${day}`;
    return `${month}/${day}/${year}`;
  }
  return '';
}

function isNumeric(n) {
  return (!Number.isNaN(parseFloat(n)) && Number.isFinite(n)) || validDate(n);
}

// A blank reference value means "no difference from the reported value",
// so fall back to the row's own value instead of sending an empty cell to the sheet.
function getReferenceValue(row) {
  const { provided, value } = row;
  if (provided === undefined || provided === null || String(provided).trim() === '') {
    return value;
  }
  return provided;
}

// Web app deployment of the Apps Script that runs the Standard Comparison sheets
// (project fuelsqc-stdcmp-v8-20261010). The previous deployment was stuck on the
// retired Rhino runtime and failed every request.
const STD_CMP_SCRIPT_ID =
  'AKfycbyNjOtSexkKqfpWSRREsT5-cGWt3oI9xlGvSx6QceOUpVqB-jHbqaK6ivW2L2PViKEQrg';

// The Apps Script answers through window.gsheetCallbackStdCmp, so the JSONP promise
// itself never resolves. If no callback arrives in this window, stop the spinner.
const SHEET_RESPONSE_TIMEOUT_MS = 90000;

// Large runs are one POST. The script can take a while to recalculate a big sheet.
const SHEET_POST_TIMEOUT_MS = 180000;

// An error reported by the Apps Script itself, whose text is safe to show the user.
class SheetResponseError extends Error {}

function getSheetErrorText(data) {
  const error = data?.error;
  if (!error) return '';
  if (typeof error === 'string') return error;
  return error.message || JSON.stringify(error);
}

function dateStringToEpoch(datestring) {
  const dateTime = +new Date(datestring);
  return dateTime ? Math.floor(dateTime / 1000) : 0;
}

function jsToExcelDate(dt) {
  const timeZoneOffset = -8; // pacific
  return ((new Date(dt).getTime() + timeZoneOffset * 3600000) / 86400000 + 25569).toFixed(0);
}

function JSDateToExcelDate(inDate) {
  if (inDate instanceof Date && !Number.isNaN(inDate.valueOf())) {
    const returnDateTime =
      25569.0 + (inDate.getTime() - inDate.getTimezoneOffset() * 60 * 1000) / (1000 * 60 * 60 * 24);
    return returnDateTime.toString().substr(0, 20);
  }
  return 0;
}

const useLoadData = () => {
  const [chartData, setChartData] = useState([]);
  const [body, setBody] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const pendingRequestRef = useRef(null);
  const mountedRef = useRef(false);
  const postControllerRef = useRef(null);

  const clearPendingRequest = () => {
    if (pendingRequestRef.current) {
      clearTimeout(pendingRequestRef.current);
      pendingRequestRef.current = null;
    }
  };

  // Cancel a large-run POST that is still in flight. Its reply is then ignored.
  const abortPendingPost = () => {
    if (postControllerRef.current) {
      postControllerRef.current.abort();
      postControllerRef.current = null;
    }
  };

  // Track mount state and drop pending requests on unmount,
  // so a late failure never shows an alert on another page.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPendingRequest();
      abortPendingPost();
    };
  }, []);

  const failRequest = (message) => {
    clearPendingRequest();
    if (!mountedRef.current) return;
    setLoading(false);
    // eslint-disable-next-line no-alert
    alert(message);
  };

  const gsheetCallbackStdCmp = async (dataIn) => {
    const data = dataIn;
    clearPendingRequest();
    setLoading(true);

    try {
      if (typeof data?.tableReturn !== 'undefined') {
        // $scope.ADRMS = data.globalReturn[5][0];
        // $scope.ADMR = data.globalReturn[6][0];
        // $scope.chartData = [];
        const newChartData = [];
        for (let i = 1; i < data.chartReturn.length; i += 1) {
          // start after header
          if (data.chartReturn[i][0] !== '#N/A') {
            for (let j = 0; j < 16; j += 1) {
              if (data.chartReturn[i][j] === '#N/A') {
                data.chartReturn[i][j] = undefined;
              }
            }
            newChartData.push({
              date: data.chartReturn[i][0],
              result: data.chartReturn[i][1],
              refHigh: data.chartReturn[i][2],
              refLow: data.chartReturn[i][3],
              rpd: data.chartReturn[i][4],
              mrRpd: data.chartReturn[i][5],
              orderedRpd: data.chartReturn[i][6],
              zScore: data.chartReturn[i][7],
              trendRpd: data.chartReturn[i][8],
              outlierRpd: data.chartReturn[i][9],
              reference: data.chartReturn[i][10],
              mrAvg: data.chartReturn[i][11],
              mrUcl: data.chartReturn[i][12],
              iAvg: data.chartReturn[i][13],
              iUcl: data.chartReturn[i][14],
              iLcl: data.chartReturn[i][15],
              i15sig: data.chartReturn[i][16],
              i15signeg: data.chartReturn[i][17],

              checkHigh: data.chartReturn[i][19],
              checkLow: data.chartReturn[i][20],
              checkEval: data.chartReturn[i][21],
              r: data.chartReturn[i][22],
              difference: data.chartReturn[i][23]
            });
          }
        }

        setChartData(newChartData);

        const t1head = [];
        let t1body = [];

        const t1colct = 11;
        for (let i = 0; i < t1colct; i += 1) {
          t1head.push(data.tableReturn[0][i]);
        }

        const localBody = [];
        for (let r = 1; r < data.tableReturn.length; r += 1) {
          const t1row = [];
          if (data.tableReturn[r][0] !== '') {
            for (let i = 0; i < t1colct; i += 1) {
              if (i === 0) {
                t1row.push(getFormattedDate(data.tableReturn[r][i]));
              } else {
                t1row.push(
                  String(data.tableReturn[r][i]).length > 5 &&
                    isNumeric(data.tableReturn[r][i]) &&
                    i !== 2
                    ? data.tableReturn[r][i].toFixed(3)
                    : decodeURI(data.tableReturn[r][i])
                );
              }
            }
            localBody.push(t1row);
          }
        }

        // sort the body:
        t1body = localBody.sort((a, b) => {
          if (!a || !a[0]) {
            return -1;
          }
          if (!b || !b[0]) {
            return 1;
          }
          if (dateStringToEpoch(a[0]) < dateStringToEpoch(b[0])) {
            return 1;
          }
          if (dateStringToEpoch(a[0]) > dateStringToEpoch(b[0])) {
            return -1;
          }
          return 0;
        });

        setBody(t1body);
        setHeaders(t1head);
      } else {
        // The Apps Script sends a payload without tableReturn when it fails.
        throw new SheetResponseError(
          getSheetErrorText(data) || 'The sheet response did not include results.'
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Product comparison: failed to process sheet response', error, data);
      if (mountedRef.current) {
        const detail = error instanceof SheetResponseError ? `\n\n${error.message}` : '';
        // eslint-disable-next-line no-alert
        alert(`Error processing product comparison results.${detail}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateChartData = async ({ viewableData, sheetId, filters }) => {
    // A new run replaces any large run still waiting on the sheet.
    abortPendingPost();
    setLoading(true);
    let numRows = 0;
    const dataBlock = [];
    const parameterBlock = [];

    const setFacilityId = 'HEP'; // I hardcoded this for now

    for (let i = 0; i < viewableData.length; i += 1) {
      numRows += 1;
      const dataObj = viewableData[i];
      dataBlock.push(dataObj.sampleID);
      dataBlock.push(dataObj.ui);
      dataBlock.push(jsToExcelDate(dataObj.date));
      dataBlock.push(encodeURIComponent(dataObj.owner));
      dataBlock.push(dataObj.batchID ? encodeURIComponent(dataObj.batchID) : '');
      dataBlock.push(dataObj.value);
      dataBlock.push(getReferenceValue(dataObj));
      dataBlock.push(dataObj.provider ? encodeURIComponent(dataObj.provider) : 'any');
      dataBlock.push(dataObj.high);
      dataBlock.push(dataObj.low);
      dataBlock.push(dataObj.omit);
      dataBlock.push(' ');
    }

    parameterBlock.push(JSDateToExcelDate(filters.minDate));
    parameterBlock.push(JSDateToExcelDate(filters.maxDate));
    parameterBlock.push(filters.testSubstanceParameter);
    parameterBlock.push(filters.owner || 'any');
    parameterBlock.push(filters.provider);
    parameterBlock.push(filters.batchID);
    parameterBlock.push(filters.uniqueID);
    parameterBlock.push(filters.testSubstance.parameter);

    if (numRows > 50) {
      // Too many rows for a JSONP URL, so POST the whole run to the script as a form.
      // The script allows cross-origin requests, so no proxy is needed.
      const controller = new AbortController();
      postControllerRef.current = controller;
      // True once a newer run or an unmount has replaced this request.
      const superseded = () => postControllerRef.current !== controller;
      const abortTimer = setTimeout(() => controller.abort(), SHEET_POST_TIMEOUT_MS);

      await fetch(`https://script.google.com/macros/s/${STD_CMP_SCRIPT_ID}/exec`, {
        method: 'POST',
        body: new URLSearchParams({
          sheetId,
          parameterBlock: parameterBlock.join('|'),
          dataBlock: dataBlock.join('|'),
          facility: setFacilityId,
          totalRows: numRows,
          numRows,
          startRow: 1,
          run: 1
        }),
        signal: controller.signal
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error(`Sheet script answered with status ${response.status}`);
        })
        .then((data) => {
          clearTimeout(abortTimer);
          if (superseded()) return undefined;
          postControllerRef.current = null;
          return gsheetCallbackStdCmp(data);
        })
        .catch((error) => {
          clearTimeout(abortTimer);
          if (superseded()) return;
          postControllerRef.current = null;
          // eslint-disable-next-line no-console
          console.error('Product comparison: request to sheet script failed', error);
          failRequest('Error in communicating data.');
        });
    } else {
      // run through sheets
      const rowChunk = 50;
      const newGSheetStdCmpRequests = [];

      for (let startRow = 1; startRow <= numRows; startRow += rowChunk) {
        let run = '';
        let thisNumRows = rowChunk;
        if (startRow + rowChunk >= numRows) {
          run = 1;
          thisNumRows = numRows + 1 - startRow;
        }
        const startIdx = (startRow - 1) * 12;
        const endIdx = (startRow - 1) * 12 + thisNumRows * 12;

        const myUrl = `https://script.google.com/macros/s/${STD_CMP_SCRIPT_ID}/exec?sheetId=${sheetId}&parameterBlock=${parameterBlock.join(
          '|'
        )}&facility=${setFacilityId}&dataBlock=${dataBlock
          .slice(startIdx, endIdx)
          .join(
            '|'
          )}&totalRows=${numRows}&numRows=${thisNumRows}&startRow=${startRow}&run=${run}&callback=gsheetCallbackStdCmp`;
        newGSheetStdCmpRequests.push(myUrl);
      }

      clearPendingRequest();
      pendingRequestRef.current = setTimeout(() => {
        failRequest('No response received from the product comparison sheet. Please try again.');
      }, SHEET_RESPONSE_TIMEOUT_MS);

      // Do not await: the JSONP promise only settles on a network error.
      jsonpCall(newGSheetStdCmpRequests[0]).catch(() => {
        failRequest('Error in communicating data.');
      });
    }
  };

  return {
    body,
    headers,
    chartData,
    updateChartData,
    gsheetCallbackStdCmp,
    loading
  };
};

export default useLoadData;
